import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import { useCreateCustomer, useCustomer, useCustomers } from "@/features/customers/hooks";
import {
  SERVICE_CONTRACT_TYPE_LABELS,
  SERVICE_STATUS_LABELS,
  type CustomerListItem,
  type ServiceContractType,
  type ServiceStatus,
} from "@/features/customers/api";
import { useCreateTicket, useTicketCategories } from "./hooks";
import { PRIORITY_LABELS } from "./labels";

// Sentinel value for the picker's "add a customer inline" option. It can never
// collide with a real customer id (those are UUIDs).
const NEW_CUSTOMER = "__new__";

const EMPTY_CUSTOMER = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  service_contract_type: "",
  service_contract_start_date: "",
  service_contract_expiry_date: "",
  service_status: "",
};

/** Tone for the service-status badge so AMC coverage is readable at a glance. */
function statusTone(s: ServiceStatus | null): "success" | "danger" | "muted" {
  if (s === "active") return "success";
  if (s === "expired") return "danger";
  return "muted";
}

/**
 * Shared new-ticket form, used by both the staff Tickets page and the
 * customer portal. A portal user never sees the customer picker — the server
 * forces their own customer_id regardless.
 *
 * Staff with `customer:create` can add a brand-new customer inline (with its
 * service-contract details), and when an existing customer is picked the form
 * surfaces that customer's service/AMC status so support can immediately see
 * whether the ticket is contract-covered or a chargeable service.
 */
export function NewTicketDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, can } = useAuth();
  const create = useCreateTicket();
  const createCustomer = useCreateCustomer();
  const { data: categories } = useTicketCategories();
  const isPortalUser = user?.customer_id != null;
  const canAddCustomer = !isPortalUser && can("customer:create");
  // Enable (and thus refetch) only while the dialog is open, so the picker is
  // always current — a customer deleted since the page loaded won't linger.
  const { data: customerPage } = useCustomers(
    { page_size: 100 },
    { enabled: open && !isPortalUser },
  );
  const customers = customerPage?.items ?? [];
  const noCustomers = open && !isPortalUser && customers.length === 0 && !canAddCustomer;

  const [form, setForm] = useState({
    title: "",
    description: "",
    customer_id: "",
    location_id: "",
    category_id: "",
    priority: "",
  });
  const [newCust, setNewCust] = useState(EMPTY_CUSTOMER);
  const [error, setError] = useState<string | null>(null);
  // "Reported by": "" = unspecified, a contact id, or "adhoc" (phone-in).
  const [reportedBy, setReportedBy] = useState("");
  const [reporter, setReporter] = useState({ name: "", email: "", phone: "" });

  // The company whose contacts we offer: the portal user's own, or the selected
  // existing customer (not when adding a brand-new one).
  const contactsCustomerId = isPortalUser
    ? user?.customer_id ?? null
    : form.customer_id && form.customer_id !== NEW_CUSTOMER
      ? form.customer_id
      : null;
  const { data: contactsCustomer } = useCustomer(open ? contactsCustomerId : null);
  const contacts = contactsCustomer?.contacts ?? [];
  // The locations the ticket can be raised for — the portal user's own company,
  // or the customer the staff member picked. A ticket must name a location.
  const locations = contactsCustomer?.locations ?? [];

  const addingCustomer = !isPortalUser && form.customer_id === NEW_CUSTOMER;
  const newCustInvalid = addingCustomer && newCust.company_name.trim().length < 2;
  const selectedCustomer: CustomerListItem | null =
    !isPortalUser && form.customer_id && form.customer_id !== NEW_CUSTOMER
      ? customers.find((c) => c.id === form.customer_id) ?? null
      : null;

  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const setCust = (k: keyof typeof newCust) => (v: string) =>
    setNewCust((c) => ({ ...c, [k]: v }));

  function reset() {
    setForm({
      title: "",
      description: "",
      customer_id: "",
      location_id: "",
      category_id: "",
      priority: "",
    });
    setNewCust(EMPTY_CUSTOMER);
    setReportedBy("");
    setReporter({ name: "", email: "", phone: "" });
  }

  // Pre-select a location: a portal user's own home location if they have one,
  // otherwise the company's primary location — so the required field is usually
  // already filled. Runs when the customer (and thus its locations) loads.
  useEffect(() => {
    if (!open || form.location_id || locations.length === 0) return;
    const mine =
      isPortalUser && user?.location_id
        ? locations.find((l) => l.id === user.location_id)
        : undefined;
    const primary = locations.find((l) => l.is_primary);
    const pick = mine ?? primary;
    if (pick) setForm((f) => ({ ...f, location_id: pick.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactsCustomer, isPortalUser, user?.location_id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      let customerId = isPortalUser ? null : form.customer_id || null;
      // Create the new customer first, then raise the ticket against it.
      if (addingCustomer) {
        if (newCust.company_name.trim().length < 2) {
          setError("Enter a company name (at least 2 characters).");
          return;
        }
        const created = await createCustomer.mutateAsync({
          company_name: newCust.company_name.trim(),
          contact_name: newCust.contact_name.trim() || null,
          email: newCust.email.trim() || null,
          phone: newCust.phone.trim() || null,
          service_contract_type:
            (newCust.service_contract_type || null) as ServiceContractType | null,
          service_contract_start_date: newCust.service_contract_start_date || null,
          service_contract_expiry_date: newCust.service_contract_expiry_date || null,
          service_status: (newCust.service_status || null) as ServiceStatus | null,
        });
        customerId = created.id;
      }
      const reporterFields =
        reportedBy === "adhoc"
          ? {
              reporter_name: reporter.name.trim() || null,
              reporter_email: reporter.email.trim() || null,
              reporter_phone: reporter.phone.trim() || null,
            }
          : reportedBy
            ? { contact_id: reportedBy }
            : {};
      await create.mutateAsync({
        title: form.title.trim(),
        description: form.description.trim(),
        customer_id: customerId,
        location_id: form.location_id || null,
        category_id: form.category_id || null,
        priority: (form.priority || null) as never,
        ...reporterFields,
      });
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  const busy = create.isPending || createCustomer.isPending;

  return (
    <Dialog open={open} onClose={onClose} title="New ticket">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <Field label="Title" htmlFor="title">
          <Input
            id="title"
            required
            minLength={3}
            value={form.title}
            onChange={(e) => set("title")(e.target.value)}
          />
        </Field>

        <Field label="Description" htmlFor="description">
          <textarea
            id="description"
            required
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={form.description}
            onChange={(e) => set("description")(e.target.value)}
          />
        </Field>

        {!isPortalUser && (
          <Field label="Customer" htmlFor="customer_id">
            <Select
              id="customer_id"
              required
              disabled={noCustomers}
              value={form.customer_id}
              onChange={(e) =>
                // Switching customers changes the available locations, so clear
                // any location picked for the previous customer.
                setForm((f) => ({ ...f, customer_id: e.target.value, location_id: "" }))
              }
            >
              <option value="">Select a customer…</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
              {canAddCustomer && <option value={NEW_CUSTOMER}>+ Add new customer…</option>}
            </Select>

            {/* Existing customer → surface their service/AMC coverage. */}
            {selectedCustomer && <ServiceStatusPanel customer={selectedCustomer} />}

            {/* New customer → full inline form. */}
            {addingCustomer && <NewCustomerFields value={newCust} onChange={setCust} />}

            {noCustomers && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                No customers yet — add one in the <strong>Customers</strong> section
                first, then raise a ticket for them.
              </p>
            )}
          </Field>
        )}

        {contactsCustomerId && (
          <Field label="Reported by" htmlFor="reported_by">
            <Select
              id="reported_by"
              value={reportedBy}
              onChange={(e) => setReportedBy(e.target.value)}
            >
              <option value="">— not specified —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.designation ? ` · ${c.designation}` : ""}
                </option>
              ))}
              <option value="adhoc">Someone else (phone-in)…</option>
            </Select>
            {reportedBy === "adhoc" && (
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <Input
                  aria-label="Reporter name"
                  placeholder="Name"
                  value={reporter.name}
                  onChange={(e) => setReporter((r) => ({ ...r, name: e.target.value }))}
                />
                <Input
                  aria-label="Reporter email"
                  placeholder="Email"
                  value={reporter.email}
                  onChange={(e) => setReporter((r) => ({ ...r, email: e.target.value }))}
                />
                <Input
                  aria-label="Reporter phone"
                  placeholder="Phone"
                  value={reporter.phone}
                  onChange={(e) => setReporter((r) => ({ ...r, phone: e.target.value }))}
                />
              </div>
            )}
          </Field>
        )}

        {contactsCustomerId && (
          <Field label="Location" htmlFor="location_id">
            {locations.length > 0 ? (
              <Select
                id="location_id"
                required
                value={form.location_id}
                onChange={(e) => set("location_id")(e.target.value)}
              >
                <option value="">Select a location…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {l.city ? ` · ${l.city}` : ""}
                    {l.is_primary ? " (primary)" : ""}
                  </option>
                ))}
              </Select>
            ) : (
              <p className="text-xs text-muted-foreground">
                This customer has no locations yet — add one on the customer's page
                first.
              </p>
            )}
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Category" htmlFor="category_id">
            <Select
              id="category_id"
              value={form.category_id}
              onChange={(e) => set("category_id")(e.target.value)}
            >
              <option value="">— none —</option>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Priority" htmlFor="priority">
            <Select
              id="priority"
              value={form.priority}
              onChange={(e) => set("priority")(e.target.value)}
            >
              <option value="">Use category default</option>
              {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              busy ||
              noCustomers ||
              !form.title.trim() ||
              !form.description.trim() ||
              (!isPortalUser && !form.customer_id) ||
              // A location is required whenever the customer has any.
              (locations.length > 0 && !form.location_id) ||
              newCustInvalid
            }
          >
            {busy ? "Creating…" : "Create ticket"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Read-only coverage summary for the selected existing customer. */
function ServiceStatusPanel({ customer }: { customer: CustomerListItem }) {
  const status = customer.service_status;
  const type = customer.service_contract_type;
  const expiry = customer.service_contract_expiry_date ?? customer.amc_expiry_date;
  const covered = status === "active";
  const known = status != null || type != null || expiry != null;

  return (
    <div className="mt-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">Service status:</span>
        {status ? (
          <Badge tone={statusTone(status)}>{SERVICE_STATUS_LABELS[status]}</Badge>
        ) : (
          <Badge tone="muted">Not set</Badge>
        )}
        {type && <span className="text-xs text-muted-foreground">· {SERVICE_CONTRACT_TYPE_LABELS[type]}</span>}
        {expiry && <span className="text-xs text-muted-foreground">· expires {expiry}</span>}
      </div>
      {known && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {covered
            ? "✅ Covered under an active contract — this ticket is not chargeable."
            : "⚠️ No active contract — this is likely a chargeable (paid) service."}
        </p>
      )}
    </div>
  );
}

/** The full inline new-customer form. */
function NewCustomerFields({
  value,
  onChange,
}: {
  value: typeof EMPTY_CUSTOMER;
  onChange: (k: keyof typeof EMPTY_CUSTOMER) => (v: string) => void;
}) {
  return (
    <div className="mt-2 space-y-3 rounded-md border border-border p-3">
      <p className="text-xs text-muted-foreground">
        A new customer will be created, then this ticket raised for it.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Company name" htmlFor="nc_company">
          <Input
            id="nc_company"
            aria-label="Company name"
            required
            autoFocus
            value={value.company_name}
            onChange={(e) => onChange("company_name")(e.target.value)}
          />
        </Field>
        <Field label="Customer name" htmlFor="nc_contact">
          <Input
            id="nc_contact"
            aria-label="Customer name"
            value={value.contact_name}
            onChange={(e) => onChange("contact_name")(e.target.value)}
          />
        </Field>
        <Field label="Customer email" htmlFor="nc_email">
          <Input
            id="nc_email"
            type="email"
            aria-label="Customer email"
            value={value.email}
            onChange={(e) => onChange("email")(e.target.value)}
          />
        </Field>
        <Field label="Contact number" htmlFor="nc_phone">
          <Input
            id="nc_phone"
            aria-label="Contact number"
            placeholder="+91 98765 43210"
            value={value.phone}
            onChange={(e) => onChange("phone")(e.target.value)}
          />
        </Field>
        <Field label="Service contract type" htmlFor="nc_type">
          <Select
            id="nc_type"
            aria-label="Service contract type"
            value={value.service_contract_type}
            onChange={(e) => onChange("service_contract_type")(e.target.value)}
          >
            <option value="">— select —</option>
            {Object.entries(SERVICE_CONTRACT_TYPE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Service status" htmlFor="nc_status">
          <Select
            id="nc_status"
            aria-label="Service status"
            value={value.service_status}
            onChange={(e) => onChange("service_status")(e.target.value)}
          >
            <option value="">— select —</option>
            {Object.entries(SERVICE_STATUS_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Contract start date" htmlFor="nc_start">
          <Input
            id="nc_start"
            type="date"
            aria-label="Contract start date"
            value={value.service_contract_start_date}
            onChange={(e) => onChange("service_contract_start_date")(e.target.value)}
          />
        </Field>
        <Field label="Contract expiry date" htmlFor="nc_expiry">
          <Input
            id="nc_expiry"
            type="date"
            aria-label="Contract expiry date"
            value={value.service_contract_expiry_date}
            onChange={(e) => onChange("service_contract_expiry_date")(e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}
